import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { isFormationPublished } from '@/lib/publicFormations';
import { downloadCSV } from '@/lib/csv';

interface FormationRow {
  id: number;
  titre: string;
  prix: number | null;
  prixpromo: string | null;
  status: string | null;
  categorie: string | null;
  image: string | null;
  created_at: string | null;
}

interface AccessRow {
  cours_id: number | null;
  status: string | null;
  revoked_at: string | null;
}

interface RequestRow {
  cours_id: number | null;
  status: string | null;
}

interface AccessDetailRow {
  iduser: number | null;
  status: string | null;
  granted_at: string | null;
  revoked_at: string | null;
}

interface LearnerRow {
  id: number;
  name: string | null;
  email: string | null;
  user_name: string | null;
}

/** Prix réellement facturé (promo prioritaire). */
function unitPrice(f: FormationRow): number {
  const promo = Number(String(f.prixpromo ?? '').replace(',', '.'));
  if (Number.isFinite(promo) && promo > 0) return promo;
  const base = Number(f.prix);
  return Number.isFinite(base) && base > 0 ? base : 0;
}

function isActive(access: AccessRow): boolean {
  if (access.revoked_at) return false;
  return (access.status || 'active').toLowerCase() !== 'revoked';
}

interface Props {
  userId: number;
  currency: string;
}

export default function TrackingTab({ userId, currency }: Props) {
  const [formations, setFormations] = useState<FormationRow[]>([]);
  const [accesses, setAccesses] = useState<AccessRow[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: fData, error: fErr } = await supabase
        .from('formations')
        .select('id, titre, prix, prixpromo, status, categorie, image, created_at')
        .eq('id_formateur', userId)
        .order('created_at', { ascending: false });
      if (fErr) throw fErr;
      const rows = (fData || []) as FormationRow[];
      setFormations(rows);

      const ids = rows.map((r) => r.id);
      if (ids.length === 0) {
        setAccesses([]);
        setRequests([]);
        return;
      }

      const [accessRes, requestRes] = await Promise.all([
        supabase.from('cours_access').select('cours_id, status, revoked_at').in('cours_id', ids),
        supabase.from('cours_access_requests').select('cours_id, status').in('cours_id', ids),
      ]);

      setAccesses((accessRes.data || []) as AccessRow[]);
      setRequests((requestRes.data || []) as RequestRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const map = new Map<number, { enrolled: number; revenue: number; pending: number }>();
    formations.forEach((f) => map.set(f.id, { enrolled: 0, revenue: 0, pending: 0 }));

    const priceOf = new Map(formations.map((f) => [f.id, unitPrice(f)]));
    accesses.filter(isActive).forEach((a) => {
      const id = Number(a.cours_id);
      const entry = map.get(id);
      if (!entry) return;
      entry.enrolled += 1;
      entry.revenue += priceOf.get(id) || 0;
    });
    requests.forEach((r) => {
      const id = Number(r.cours_id);
      const entry = map.get(id);
      if (!entry) return;
      if ((r.status || '').toLowerCase() === 'pending') entry.pending += 1;
    });
    return map;
  }, [formations, accesses, requests]);

  const totals = useMemo(() => {
    let enrolled = 0;
    let revenue = 0;
    let pending = 0;
    let published = 0;
    formations.forEach((f) => {
      const s = stats.get(f.id);
      if (s) {
        enrolled += s.enrolled;
        revenue += s.revenue;
        pending += s.pending;
      }
      if (isFormationPublished(f.status)) published += 1;
    });
    return { enrolled, revenue, pending, published };
  }, [formations, stats]);

  const togglePublish = async (f: FormationRow) => {
    setBusyId(f.id);
    const next = isFormationPublished(f.status) ? 'draft' : 'published';
    try {
      const { error: updErr } = await supabase.from('formations').update({ status: next }).eq('id', f.id);
      if (updErr) throw updErr;
      setFormations((prev) => prev.map((row) => (row.id === f.id ? { ...row, status: next } : row)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mise à jour impossible.');
    } finally {
      setBusyId(null);
    }
  };

  const money = (value: number) => `${Math.round(value).toLocaleString('fr-FR')} ${currency}`;

  /** Exporte la liste des inscrits d'une formation au format CSV. */
  const exportEnrollees = async (f: FormationRow) => {
    setExportingId(f.id);
    setError(null);
    try {
      const { data, error: accessErr } = await supabase
        .from('cours_access')
        .select('iduser, status, granted_at, revoked_at')
        .eq('cours_id', f.id)
        .order('granted_at', { ascending: false });
      if (accessErr) throw accessErr;

      const rows = (data || []) as AccessDetailRow[];
      if (rows.length === 0) {
        setError('Aucun inscrit à exporter pour cette formation.');
        return;
      }

      const ids = Array.from(
        new Set(
          rows
            .map((r) => Number(r.iduser))
            .filter((n) => Number.isFinite(n) && n > 0),
        ),
      );

      const people = new Map<number, LearnerRow>();
      if (ids.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, name, email, user_name')
          .in('id', ids);
        ((usersData || []) as LearnerRow[]).forEach((u) => people.set(Number(u.id), u));
      }

      const price = unitPrice(f);
      const csvRows = rows.map((r) => {
        const person = people.get(Number(r.iduser));
        const status = r.revoked_at ? 'Révoqué' : (r.status || 'active').toLowerCase() === 'active' ? 'Actif' : (r.status || '');
        return [
          person?.name || person?.user_name || `Utilisateur ${r.iduser ?? '—'}`,
          person?.email || '',
          status,
          r.granted_at ? new Date(r.granted_at).toLocaleString('fr-FR') : '',
          r.revoked_at ? new Date(r.revoked_at).toLocaleString('fr-FR') : '',
          price > 0 ? String(price) : 'Gratuit',
        ];
      });

      const slug = f.titre
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50) || `formation-${f.id}`;

      downloadCSV(
        `inscrits-${slug}.csv`,
        ['Nom', 'Email', 'Statut', 'Date d\'inscription', 'Date de révocation', `Montant (${currency})`],
        csvRows,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export impossible.');
    } finally {
      setExportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold font-heading text-foreground-950">
            <i className="ri-bar-chart-box-line mr-2 text-primary-500"></i>
            Formations publiées
          </h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            Suivez les inscrits et les revenus générés par chacune de vos formations.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {error && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700 flex-1">
            <i className="ri-error-warning-line mr-1.5"></i>
            {error}
          </p>
          <button
            onClick={() => void load()}
            className="px-4 py-2 rounded-full bg-red-600 text-white text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {[
          { icon: 'ri-book-open-line', label: 'Formations publiées', value: String(totals.published), tone: 'bg-primary-100 text-primary-700' },
          { icon: 'ri-user-add-line', label: 'Inscrits au total', value: String(totals.enrolled), tone: 'bg-accent-100 text-accent-700' },
          { icon: 'ri-money-dollar-circle-line', label: 'Revenus générés', value: money(totals.revenue), tone: 'bg-secondary-100 text-secondary-700' },
          { icon: 'ri-time-line', label: 'Demandes en attente', value: String(totals.pending), tone: 'bg-background-100 text-foreground-600' },
        ].map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${card.tone}`}>
              <i className={`${card.icon} text-lg`}></i>
            </span>
            <p className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">{card.value}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {formations.length === 0 ? (
        <div className="flex flex-col items-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-graduation-cap-line text-4xl text-foreground-300 mb-3"></i>
          <p className="text-sm text-foreground-500">Aucune formation pour le moment.</p>
          <p className="text-xs text-foreground-400 mt-1">Créez votre première formation depuis l&apos;onglet « Mes formations ».</p>
        </div>
      ) : (
        <div className="space-y-3">
          {formations.map((f) => {
            const s = stats.get(f.id) || { enrolled: 0, revenue: 0, pending: 0 };
            const published = isFormationPublished(f.status);
            return (
              <div key={f.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-background-100 flex items-center justify-center flex-shrink-0">
                      {f.image ? (
                        <img src={f.image} alt={f.titre} title={f.titre} className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-graduation-cap-line text-xl text-foreground-300"></i>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground-900 truncate">{f.titre}</h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                            published ? 'bg-accent-100 text-accent-800' : 'bg-background-100 text-foreground-500'
                          }`}
                        >
                          {published ? 'Publiée' : 'Brouillon'}
                        </span>
                        {s.pending > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-secondary-100 text-secondary-800 whitespace-nowrap">
                            {s.pending} demande(s)
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-foreground-400 mt-1">
                        {f.categorie || 'Sans catégorie'} · {unitPrice(f) > 0 ? money(unitPrice(f)) : 'Gratuit'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 lg:w-64 flex-shrink-0">
                    <div className="rounded-lg bg-background-100 px-3 py-2">
                      <p className="text-[10px] text-foreground-500">Inscrits</p>
                      <p className="text-sm font-bold text-foreground-950">{s.enrolled}</p>
                    </div>
                    <div className="rounded-lg bg-background-100 px-3 py-2">
                      <p className="text-[10px] text-foreground-500">Revenus</p>
                      <p className="text-sm font-bold text-foreground-950 truncate">{money(s.revenue)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    <button
                      onClick={() => void exportEnrollees(f)}
                      disabled={exportingId === f.id}
                      title="Exporter la liste des inscrits en CSV"
                      className="px-4 py-2 rounded-full bg-secondary-100 text-secondary-800 text-xs font-semibold whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                    >
                      {exportingId === f.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <><i className="ri-file-download-line mr-1"></i>CSV</>
                      )}
                    </button>
                    <Link
                      to={`/dashboard/formation/${f.id}`}
                      className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-settings-3-line mr-1"></i>
                      Gérer
                    </Link>
                    <button
                      onClick={() => void togglePublish(f)}
                      disabled={busyId === f.id}
                      className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 ${
                        published
                          ? 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                          : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      }`}
                    >
                      {busyId === f.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : published ? (
                        <><i className="ri-eye-off-line mr-1"></i>Dépublier</>
                      ) : (
                        <><i className="ri-send-plane-line mr-1"></i>Publier</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}